import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import Layout from '../../../components/feature/Layout';
import PageBanner from '../../../components/feature/PageBanner';
import { Link } from 'react-router-dom';

const additionTypes = [
  { icon: 'ri-hotel-line', title: 'Room Additions', desc: 'Extra bedrooms, sunrooms, playrooms — we design and build seamless additions that match your existing home\'s architecture perfectly.' },
  { icon: 'ri-building-2-line', title: 'Second Story Additions', desc: 'Double your square footage by going up. Our structural engineers ensure a safe, beautiful vertical expansion of your home.' },
  { icon: 'ri-car-line', title: 'Garage Additions', desc: 'Attached single, double, or triple car garages that blend seamlessly with your home\'s exterior and add serious resale value.' },
  { icon: 'ri-home-heart-line', title: 'In-Law Suites', desc: 'Private, comfortable living spaces for family or rental income — complete with kitchen, bath, and separate entrance.' },
];

const stats = [
  { value: '29+', label: 'Years Experience' },
  { value: '100+', label: 'Additions Completed' },
  { value: '100%', label: 'Client Satisfaction' },
  { value: '$0', label: 'Hidden Fees' },
];

const process = [
  { num: '01', icon: 'ri-pencil-ruler-2-line', title: 'Design Consultation', desc: 'We meet on-site, measure carefully, and co-create an addition plan that fits your lifestyle and budget.' },
  { num: '02', icon: 'ri-file-list-3-line', title: 'Permit & Approval', desc: 'We handle all required permits and local building department approvals so you stay stress-free.' },
  { num: '03', icon: 'ri-building-line', title: 'Foundation & Framing', desc: 'Every addition starts with a proper foundation and precision framing that connects cleanly to your existing structure.' },
  { num: '04', icon: 'ri-paint-brush-line', title: 'Finishing & Handover', desc: 'Insulation, drywall, paint, flooring, trim — we finish to the same standard as the rest of your home, inside and out.' },
];

export default function AdditionsPage() {
  const { ref: typesRef, visible: typesVis } = useScrollAnimation();
  const { ref: procRef, visible: procVis } = useScrollAnimation();
  const { ref: statsRef, visible: statsVis } = useScrollAnimation();

  return (
    <Layout>
      <PageBanner
        title="Home Additions"
        subtitle="Need more space? We design and build high-quality home additions that match your existing structure and add lasting value to your property."
        breadcrumb="Home Additions"
        imageUrl="https://public.readdy.ai/ai/img_res/0a793a82988d5f53cb845e441b174e29.jpg"
        accentWord="Additions"
      />

      {/* Stats Bar */}
      <section className="bg-gradient-to-r from-primary to-primary-mid py-10 sm:py-12">
        <div ref={statsRef} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className="text-center"
              style={{ opacity: statsVis ? 1 : 0, transform: statsVis ? 'translateY(0)' : 'translateY(20px)', transition: `all 0.6s ease ${i * 100}ms` }}
            >
              <div className="text-3xl sm:text-4xl font-black text-white mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{stat.value}</div>
              <div className="text-primary-light/80 text-xs uppercase tracking-widest font-semibold">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Types of Additions */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="text-center mb-12 sm:mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">Addition Types</span>
              <div className="w-8 h-0.5 bg-primary" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-gray-900 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              What We Can <span className="text-primary">Build For You</span>
            </h2>
          </div>
          <div ref={typesRef} className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {additionTypes.map((type, i) => (
              <div
                key={type.title}
                className="flex gap-5 sm:gap-6 p-6 sm:p-7 bg-white border border-gray-100 rounded-lg hover-card hover:border-primary-border"
                style={{ opacity: typesVis ? 1 : 0, transform: typesVis ? 'translateY(0)' : 'translateY(32px)', transition: `all 0.7s ease ${i * 120}ms` }}
              >
                <div className="w-13 h-13 sm:w-14 sm:h-14 flex-shrink-0 flex items-center justify-center bg-primary-light border border-primary-border rounded-lg">
                  <i className={`${type.icon} text-primary text-xl sm:text-2xl`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 uppercase tracking-wide mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{type.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{type.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Image + Content */}
      <section className="bg-primary-dark py-16 sm:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="relative">
              <div className="w-full h-[360px] sm:h-[420px] rounded-lg overflow-hidden img-zoom">
                <img src="https://readdy.ai/api/search-image?query=finished%20room%20addition%20on%20home%20with%20new%20sunroom%20and%20large%20windows%2C%20professionally%20built%2C%20bright%20interior%2C%20hardwood%20floors%2C%20white%20trim%2C%20matching%20exterior%20siding%2C%20suburban%20house%20Michigan&width=700&height=500&seq=add02&orientation=portrait" alt="Completed home addition" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-white border border-gray-100 rounded-lg p-4 sm:p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary rounded-lg flex-shrink-0">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-gray-900 text-sm font-bold">Licensed & Insured</div>
                    <div className="text-gray-500 text-xs">Michigan Contractor</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-0.5 bg-primary-light" />
              <span className="text-primary-light text-xs font-bold uppercase tracking-[0.3em]">Our Approach</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white leading-tight tracking-tight mb-5 sm:mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Seamless Additions That Look <span className="text-primary-light">Like They Were Always There</span>
            </h2>
            <p className="text-blue-200/60 text-sm sm:text-base leading-relaxed mb-5">
              The hardest part of a home addition isn't building the new space — it's making it look like it was always part of the original structure. That's where our 29+ years of experience shows.
            </p>
            <p className="text-blue-200/60 text-sm sm:text-base leading-relaxed mb-7 sm:mb-8">
              We meticulously match rooflines, siding, trim, and interior finishes. The result is an addition your neighbors won't even notice wasn't always there.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-primary-mid hover:bg-primary text-white font-bold uppercase tracking-widest text-sm px-7 py-4 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press group">
              Start Your Addition
              <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="text-center mb-12 sm:mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">Our Process</span>
              <div className="w-8 h-0.5 bg-primary" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-gray-900 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              From Idea to <span className="text-primary">Move-In</span>
            </h2>
          </div>
          <div ref={procRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {process.map((step, i) => (
              <div
                key={step.num}
                className="relative text-center p-6 sm:p-7"
                style={{ opacity: procVis ? 1 : 0, transform: procVis ? 'translateY(0)' : 'translateY(32px)', transition: `all 0.7s ease ${i * 120}ms` }}
              >
                <div className="text-7xl sm:text-8xl font-black text-gray-100 absolute top-4 left-1/2 -translate-x-1/2 leading-none pointer-events-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{step.num}</div>
                <div className="relative z-10">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary rounded-full mx-auto mb-4">
                    <i className={`${step.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{step.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary to-primary-mid">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Ready to Add More Space?
          </h2>
          <p className="text-blue-100/80 mb-8 text-sm sm:text-base max-w-xl mx-auto">We build additions that add genuine value and beauty to your home. Let's talk about your project.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-primary-light transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              Get Free Estimate
            </Link>
            <a href="tel:269-599-4913" className="border-2 border-white/60 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              (269) 599-4913
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
